import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiCreditCard, FiX } from 'react-icons/fi';
import { useNotification } from '../context/NotificationContext';
import {
  AdminBankAccount,
  AdminBankAccountsService,
  BankAccountStatus
} from '../services/adminBankAccounts.service';
import styles from './AdminBankAccountsPage.module.css';

const statusFilters: BankAccountStatus[] = ['PENDING', 'VERIFIED', 'REJECTED', 'DISABLED'];

const statusLabelMap = (t: (key: string) => string) => ({
  PENDING: t('admin.bank_accounts_status_pending') || 'Pendiente',
  VERIFIED: t('admin.bank_accounts_status_verified') || 'Verificada',
  REJECTED: t('admin.bank_accounts_status_rejected') || 'Rechazada',
  DISABLED: t('admin.bank_accounts_status_disabled') || 'Inactiva'
});

export default function AdminBankAccountsPage() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [items, setItems] = useState<AdminBankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<BankAccountStatus>('PENDING');
  const [query, setQuery] = useState('');
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<AdminBankAccount | null>(null);

  const load = async (override?: { status?: BankAccountStatus; q?: string }) => {
    setLoading(true);
    try {
      const res = await AdminBankAccountsService.list({
        status: override?.status ?? filter,
        q: (override?.q ?? query.trim()) || undefined
      });
      setItems(Array.isArray(res) ? res : []);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ status: filter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleSearch = () => {
    load();
  };

  const handleVerify = async (item: AdminBankAccount) => {
    if (item.status !== 'PENDING') return;
    setVerifyTarget(item);
  };

  const confirmVerify = async () => {
    if (!verifyTarget || verifyTarget.status !== 'PENDING') return;

    setSubmittingId(verifyTarget.id);
    try {
      await AdminBankAccountsService.verify(verifyTarget.id);
      addNotification(t('admin.bank_accounts_verify_success') || 'Cuenta bancaria verificada.', 'success');
      setVerifyTarget(null);
      await load();
    } catch (error: any) {
      addNotification(error?.message || t('admin.bank_accounts_verify_error') || t('common.error_generic'), 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  const statusLabel = useMemo(() => statusLabelMap(t), [t]);

  const statusClass = (status: BankAccountStatus) => {
    const map: Record<BankAccountStatus, string> = {
      PENDING: styles.badgePending,
      VERIFIED: styles.badgeVerified,
      REJECTED: styles.badgeRejected,
      DISABLED: styles.badgeDisabled
    };
    return map[status] || '';
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('admin.bank_accounts_title') || 'Cuentas bancarias'}</h1>
          <p className={styles.subtitle}>
            {t('admin.bank_accounts_subtitle') || 'Aprueba CLABEs para habilitar retiros en pruebas.'}
          </p>
        </div>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          {statusFilters.map((status) => (
            <button
              key={status}
              className={filter === status ? styles.filterActive : styles.filterButton}
              onClick={() => setFilter(status)}
            >
              {statusLabel[status] || status}
            </button>
          ))}
        </div>
        <div className={styles.searchRow}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={
              t('admin.bank_accounts_search_placeholder') ||
              'Buscar por usuario, email o CLABE...'
            }
          />
          <button type="button" onClick={handleSearch} className={styles.searchButton}>
            {t('admin.search') || t('common.search') || 'Buscar'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>{t('common.loading') || 'Cargando...'}</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>{t('admin.bank_accounts_empty') || 'Sin cuentas para este filtro.'}</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('admin.bank_accounts_table_user') || 'Usuario'}</th>
                <th>{t('admin.bank_accounts_table_bank') || 'Banco'}</th>
                <th>{t('admin.bank_accounts_table_clabe') || 'CLABE'}</th>
                <th>{t('admin.bank_accounts_table_status') || 'Estado'}</th>
                <th>{t('admin.bank_accounts_table_created') || 'Registrada'}</th>
                <th>{t('admin.bank_accounts_table_actions') || 'Acciones'}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const userName = item.user
                  ? `${item.user.first_name ?? ''} ${item.user.last_name ?? ''}`.trim() || '—'
                  : '—';
                const email = item.user?.email || '—';
                const bankName = item.bank?.name || '—';

                return (
                  <tr key={item.id}>
                    <td>
                      <div className={styles.userName}>{userName}</div>
                      <div className={styles.userEmail}>{email}</div>
                    </td>
                    <td>
                      <div className={styles.bankName}>{bankName}</div>
                      <div className={styles.bankMeta}>{item.beneficiaryName}</div>
                    </td>
                    <td className={styles.mono}>
                      {item.clabeMasked || '—'}
                      {item.isDefault ? (
                        <span className={styles.defaultBadge}>
                          {t('admin.bank_accounts_table_default') || 'Predeterminada'}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${statusClass(item.status)}`}>
                        {statusLabel[item.status] || item.status}
                      </span>
                    </td>
                    <td>{new Date(item.createdAt).toLocaleDateString('es-MX')}</td>
                    <td>
                      {item.status === 'PENDING' ? (
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => handleVerify(item)}
                          disabled={submittingId === item.id}
                        >
                          {submittingId === item.id
                            ? t('common.processing') || 'Procesando...'
                            : t('admin.bank_accounts_verify_action') || 'Aprobar'}
                        </button>
                      ) : (
                        <span className={styles.mutedAction}>
                          {t('admin.bank_accounts_verified') || 'Verificada'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {verifyTarget ? (
        <div
          className={styles.modalOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="verify-bank-account-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !submittingId) setVerifyTarget(null);
          }}
        >
          <div className={styles.modal}>
            <div className={styles.modalAccent} />
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleRow}>
                <div className={styles.modalIcon}>
                  <FiCreditCard />
                </div>
                <div>
                  <p className={styles.modalEyebrow}>Revisión bancaria</p>
                  <h2 id="verify-bank-account-title" className={styles.modalTitle}>
                    Aprobar cuenta bancaria
                  </h2>
                </div>
              </div>
              <button
                type="button"
                className={styles.modalCloseButton}
                onClick={() => setVerifyTarget(null)}
                disabled={!!submittingId}
                aria-label="Cerrar modal"
              >
                <FiX />
              </button>
            </div>

            <p className={styles.modalText}>
              Al aprobar esta cuenta, el usuario podrá usarla como cuenta validada para procesos bancarios dentro de la plataforma.
            </p>

            <div className={styles.modalDetails}>
              <div>
                <span>Usuario</span>
                <strong>
                  {verifyTarget.user
                    ? `${verifyTarget.user.first_name ?? ''} ${verifyTarget.user.last_name ?? ''}`.trim() || '—'
                    : '—'}
                </strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{verifyTarget.user?.email || '—'}</strong>
              </div>
              <div>
                <span>Banco</span>
                <strong>{verifyTarget.bank?.name || '—'}</strong>
              </div>
              <div>
                <span>Titular</span>
                <strong>{verifyTarget.beneficiaryName || '—'}</strong>
              </div>
              <div>
                <span>CLABE</span>
                <strong className={styles.mono}>{verifyTarget.clabeMasked || '—'}</strong>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setVerifyTarget(null)}
                disabled={!!submittingId}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={confirmVerify}
                disabled={!!submittingId}
              >
                <FiCheckCircle />
                {submittingId === verifyTarget.id ? 'Aprobando...' : 'Aprobar cuenta'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
